import type {StateCreator} from 'zustand'

export type ShowActionBarFn = (cellId: string) => void
export type HideActionBarFn = (cellId: string) => void
export type HideAllActionBarsFn = () => void
export type SetFocusedActionBarFn = (cellId: string) => void
export type ClearFocusedActionBarFn = () => void

export interface ActionBarVisibilitySlice {
  actionBarVisibility: {
    activeActionBarCellId: string | null
    focusedActionBarCellId: string | null
  }
  showActionBar: ShowActionBarFn
  hideActionBar: HideActionBarFn
  hideAllActionBars: HideAllActionBarsFn
  setFocusedActionBar: SetFocusedActionBarFn
  clearFocusedActionBar: ClearFocusedActionBarFn
}

export const createActionBarVisibilitySlice =
  (): StateCreator<ActionBarVisibilitySlice, [['zustand/devtools', never]]> => set => ({
    actionBarVisibility: {
      activeActionBarCellId: null,
      focusedActionBarCellId: null,
    },
    showActionBar: (cellId: string) =>
      set(
        state => ({
          actionBarVisibility: {
            activeActionBarCellId: cellId,
            focusedActionBarCellId: state.actionBarVisibility.focusedActionBarCellId,
          },
        }),
        undefined,
        `actionBarVisibility#show:${cellId}`,
      ),
    hideActionBar: (cellId: string) =>
      set(
        state => {
          // Only hide if this cellId is currently the active one (prevents race conditions)
          if (state.actionBarVisibility.activeActionBarCellId === cellId) {
            return {
              actionBarVisibility: {
                activeActionBarCellId: null,
                focusedActionBarCellId: state.actionBarVisibility.focusedActionBarCellId,
              },
            }
          }
          return state
        },
        undefined,
        `actionBarVisibility#hide:${cellId}`,
      ),
    hideAllActionBars: () =>
      set(
        () => ({
          actionBarVisibility: {
            activeActionBarCellId: null,
            focusedActionBarCellId: null,
          },
        }),
        undefined,
        `actionBarVisibility#hideAll`,
      ),
    setFocusedActionBar: (cellId: string) =>
      set(
        state => ({
          actionBarVisibility: {
            activeActionBarCellId: state.actionBarVisibility.activeActionBarCellId,
            focusedActionBarCellId: cellId,
          },
        }),
        undefined,
        `actionBarVisibility#setFocused:${cellId}`,
      ),
    clearFocusedActionBar: () =>
      set(
        state => ({
          actionBarVisibility: {
            activeActionBarCellId: state.actionBarVisibility.activeActionBarCellId,
            focusedActionBarCellId: null,
          },
        }),
        undefined,
        `actionBarVisibility#clearFocused`,
      ),
  })
